/* Spieler endgültig löschen — die Entscheidungen und der Ablauf.
 *
 * Reine Logik, kein DOM, kein Supabase: Alles Äußere kommt über `io` herein
 * (composables/usePlayers.js reicht die echten Aufrufe aus src/supabase/ hinein, die Tests
 * Stellvertreter). Wer löschen DARF, entscheidet die Datenbank (players_delete, Speicherregel
 * player_files_delete: nur admin); die Oberfläche blendet den Knopf nur aus.
 *
 * WAS PASSIERT (Einzelheiten und Tabelle: docs/spieler-loeschen-bericht.md)
 *   - Die Datenbank nimmt beim Löschen der Zeile mit: player_contracts, player_contract_files,
 *     player_transfers (Fremdschlüssel on delete cascade). Alles Übrige steckt im Datenblock
 *     `players.data` und verschwindet mit der Zeile.
 *   - NICHT mitgelöscht werden Dateien im Speicher (kein Fremdschlüssel möglich) und Meldungen
 *     (notifications.player_id ist Text ohne Bindung). Beides erledigt dieser Ablauf.
 *
 * REIHENFOLGE — erst sammeln, dann die Zeile, dann die Dateien
 *   1. Sammeln (nur lesen): Dateiliste aus Datenblock, Vertragsdateien, Transferlogos und den
 *      Ordnern des Spielers im Speicher. Scheitert das, bricht alles ab: nichts ist verändert.
 *   2. Datenbankzeile löschen. Das ist der Punkt ohne Rückweg. Scheitert es (oder löscht die
 *      Datenbank 0 Zeilen, weil die Rolle nicht darf), bricht alles ab: Spieler UND Dateien
 *      sind unverändert.
 *   3. Dateien löschen. Scheitern einzelne, bleibt eine unsichtbare, nicht mehr verknüpfte
 *      Datei liegen (Platz, kein Schaden); der Aufrufer bekommt die Zahl gemeldet.
 *   4. Meldungen zum Spieler löschen (Fehler wird gemeldet, bricht nichts ab).
 *   Warum nicht Dateien zuerst: Scheiterte danach das Löschen der Zeile, stünde ein Spieler mit
 *   toten Verweisen (Foto, Verträge) da — ein halber Zustand, der Daten kostet. Umgekehrt
 *   kostet ein Fehler in Schritt 3 nur Speicherplatz.
 *
 * GESCHÜTZT bleibt, was ein anderer Spieler noch verwendet: Vereinslogos (auch aus dessen
 * Transferhistorie), Fotos, Dateien — jeder Pfad, der im Datenblock oder in den Transfers eines
 * anderen Spielers steht.
 */

import { derivedCandidates, VARIANT_NAMES } from './photoVariants.js';

/* Oberste Ordner im Speicher, die nie zu einem Spieler gehören */
export const NON_PLAYER_FOLDERS = ['user-avatars'];

/**
 * Fehlercode des Löschens → Schlüssel unter errors.* (Anzeige in DeletePlayerButton.vue).
 * 'deleteLogFailed': Das Löschprotokoll ließ sich nicht schreiben, die Datenbank hat das Löschen
 * abgebrochen — nichts ist verändert. Alles Unbekannte bleibt allgemein.
 */
export function deleteErrorKey(code) {
  return {
    notAllowed: 'deletePlayerNotAllowed',
    deleteLogFailed: 'deletePlayerLogFailed',
  }[code] || 'deletePlayerFailed';
}

/** Das Wort, das man zur Bestätigung eintippen muss: Nachname, sonst Vorname, sonst "DELETE". */
export function deleteConfirmWord(player) {
  const last = String(player?.lastName ?? '').trim();
  if (last) return last;
  const first = String(player?.firstName ?? '').trim();
  return first || 'DELETE';
}

/** Stimmt die Eingabe mit dem Bestätigungswort überein? Ohne Groß-/Kleinschreibung und Randleerzeichen. */
export function matchesConfirmWord(input, word) {
  const a = String(input ?? '').trim().toLowerCase();
  const b = String(word ?? '').trim().toLowerCase();
  return b.length > 0 && a === b;
}

const looksLikePath = (s) => typeof s === 'string' && /^[^/\s][^\s]*\/[^\s]+$/.test(s) && !/^(https?|data|blob):/i.test(s);

/**
 * Alle Speicherpfade, die irgendwo in einem Wert stehen: unter Schlüsseln `path` oder
 * `...Path` (avatarPath, bioPhotoPath, currentClubLogoPath, path an Datei-Objekten …).
 * Durchsucht verschachtelte Objekte und Listen.
 */
export function collectPaths(value, out = new Set(), key = '') {
  if (value == null) return out;
  if (typeof value === 'string') {
    if ((key === 'path' || /Path$/.test(key)) && looksLikePath(value)) out.add(value);
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((v) => collectPaths(v, out, key));
    return out;
  }
  if (typeof value === 'object') {
    for (const [k, v] of Object.entries(value)) collectPaths(v, out, k);
  }
  return out;
}

/** Ein Pfad samt möglicher verkleinerter Fassungen (Fotos). Gibt es sie nicht, schadet das Löschen nicht. */
export function withVariants(path) {
  return [path, ...VARIANT_NAMES.flatMap((n) => derivedCandidates(path, n))];
}

/** Pfade, die ein anderer Spieler noch verwendet (samt Fassungen) — nie löschen. */
export function protectedPaths(others = []) {
  const keep = new Set();
  for (const o of others) {
    for (const p of collectPaths(o)) withVariants(p).forEach((x) => keep.add(x));
  }
  return keep;
}

/**
 * Was gelöscht werden soll.
 *   player              der Spieler (Datenblock) — Fotos, Karte, Dokumente, Reise, Videos …
 *   contractFilePaths   Pfade aus player_contract_files
 *   transfers           Einträge der Transferhistorie (Logos)
 *   listed              Pfade, die im Speicher unter den Ordnern des Spielers liegen
 *   others              alle übrigen Spieler (Schutz)
 *   foreignLogoPaths    Pfade, die die Transferhistorie ANDERER Spieler nennt (Schutz)
 * @returns {string[]}
 */
export function filesToRemove({
  player, contractFilePaths = [], transfers = [], listed = [], others = [], foreignLogoPaths = [],
}) {
  const candidates = new Set();
  const add = (p) => { if (looksLikePath(p)) withVariants(p).forEach((x) => candidates.add(x)); };

  collectPaths(player).forEach(add);
  contractFilePaths.forEach(add);
  for (const t of transfers) {
    add(t.fromClubLogoPath);
    add(t.toClubLogoPath);
  }
  listed.forEach((p) => { if (looksLikePath(p)) candidates.add(p); });

  const keep = protectedPaths(others);
  foreignLogoPaths.forEach((p) => withVariants(p).forEach((x) => keep.add(x)));

  return [...candidates].filter((p) => !keep.has(p)).sort();
}

/**
 * Der ganze Ablauf. io = {
 *   collect(playerId) -> Promise<{ contractFilePaths, transfers, listed }>   (nur lesen; wirft bei Fehler)
 *   foreignLogoRefs(paths, playerId) -> Promise<string[]>                    (Pfade, die andere Spieler nennen; wirft bei Fehler)
 *   registerLogoRefs(paths) -> Promise<string[]>                             (Pfade, die das Klubregister als Logo nennt; wirft bei Fehler)
 *   deleteRow(playerId) -> Promise<number>                                   (Anzahl gelöschter Zeilen; wirft bei Fehler)
 *   removeFiles(paths) -> Promise<{ failed: string[] }>
 *   deleteNotifications(playerId) -> Promise<void>                           (wirft bei Fehler)
 * }
 * Wirft Error('loadFailed') (Sammeln gescheitert), Error('notAllowed') (Datenbank löschte nichts),
 * sonst den Fehler von deleteRow — in allen drei Fällen ist NICHTS verändert. Danach wirft nichts mehr.
 * @returns {Promise<{ filesFound: number, filesFailed: string[], notificationsFailed: boolean }>}
 */
export async function deletePlayerFlow({ player, others = [], io, confirmed }) {
  /* Kein Löschen ohne Bestätigung im Code-Pfad: Der Aufrufer muss sie ausdrücklich bestätigen. */
  if (confirmed !== true) throw new Error('notConfirmed');
  if (!player || !player.id || player._isNew === true || typeof player.id === 'number') {
    throw new Error('notFound');
  }

  let paths;
  try {
    const found = await io.collect(player.id);
    let foreign = [];
    const first = filesToRemove({ player, ...found, others });
    if (first.length) {
      foreign = await io.foreignLogoRefs(first, player.id);
      /* Logos des Klubregisters gehören keinem Spieler: nie löschen. Fehlt die Funktion oder schlägt die Abfrage
         fehl, wirft das hier — der Ablauf bricht ab, BEVOR etwas gelöscht wird (fail closed). */
      foreign = [...foreign, ...(await io.registerLogoRefs(first))];
    }
    paths = filesToRemove({ player, ...found, others, foreignLogoPaths: foreign });
  } catch (e) {
    console.error('[delete-player] Sammeln fehlgeschlagen:', e && e.message);
    throw new Error('loadFailed');
  }

  const rows = await io.deleteRow(player.id);
  if (!(rows > 0)) throw new Error('notAllowed');

  let filesFailed = [];
  try {
    filesFailed = (await io.removeFiles(paths)).failed || [];
  } catch (e) {
    console.error('[delete-player] Dateien löschen fehlgeschlagen:', e && e.message);
    filesFailed = paths;
  }

  let notificationsFailed = false;
  try {
    await io.deleteNotifications(player.id);
  } catch (e) {
    console.error('[delete-player] Meldungen löschen fehlgeschlagen:', e && e.message);
    notificationsFailed = true;
  }

  return { filesFound: paths.length, filesFailed, notificationsFailed };
}
